/**
 * `Model` is abstract class used to create any models (product, category, error, static, .. )
 * Model can be pretty much anything
 *
 * # Model type
 *
 * Each model needs public `type` string property set. This is to distinguish what kind of model it is.
 * Example: product, category, ..
 *
 * This variable is used in many places of code and is absolutely required.
 *
 * Note that type determines from what mongo table data is loaded.
 *
 * # Routable models
 *
 * If model is routable it requires private property _routable = true.
 * Everytime instance of this model is loaded its path is registered in resource
 *
 *
 * # Model id
 *
 * Id can be either string or number. It's main way to load and identify models.
 *
 *
 * # Singleton model
 *
 * When model has as only one instance (like customer) then it does not require id.
 * Such model is singleton and we can create it by registration in resource with singleton : true option.
 * Any other model will require id to be identified.
 *
 *
 * ## Creating model class
 *
 * This is product class example.
 * `Product` class is created simply by extension of `Model`.
 * We declare required `type` and for this kind of model we add _routable flag also.
 * After declaration we register our model in resource (other way it can be used, but not with resource)
 * Note that Resource is static class.
 *
 *
 * export class Product<T> extends Model<T> {
 *      public type = 'product';
 *      protected _routable = true;
 * }
 *
 * Resource.register({
 *      typeName: 'product',
 *      instanceClass :  Product
 * });
 *
 */
/**
 * Loads subscription data on page init
 */

import { RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { MultisiteService } from '@df/core/service/multisite';
import { NgInject } from '@df/ng/ng-inject';
import defaults from 'lodash-es/defaults';
import isEqual from 'lodash-es/isEqual';
import { Subject } from 'rxjs';
import { DependencyLoader } from './model/dependency-loader';
import { IfInStockDirectiveDef } from '@df/catalog-ui/stock/if-in-stock.directive.def';

export interface IModel {
    id: number | string;
    type: string;
    verbosity?: number;
    data?: IModelData;
}

export interface IModelDataV1<TId extends number | string = number | string> {
    id: TId;
    verbosity: number;
    type: string;
    store?: number;
}

export interface IModelDataV2<TId extends number | string = number | string> extends IModelDataV1<TId> {
    breadcrumbs?: ICrumb[];
}

export interface IModelDataV3<TId extends number | string = number | string> extends IModelDataV2<TId> {
    meta?: IMetaData;
}

export interface IModelDataV3WithUrl extends IModelDataV3 {
    url: string;
}
export interface IModelDataV2WithUrl extends IModelDataV2 {
    url: string;
}

export declare type IModelData = IModelDataV1 | IModelDataV2 | IModelDataV3;

/**
 * Model status
 */
export enum ModelStatus {
    disabled = 0,
    enabled = 1,
    deleted = 2
}

export interface ICrumb {
    url: string;
    label: string;
}

export interface IMetaData {
    title?: string;
    description?: string;
    canonical?: string;
    nosearch?: boolean;
    ogtype?: string;
    ogimage?: string;
    ogimageType?: string;
    robots?: string;
    alternates?: IAlternate[];
}

export interface IAlternate {
    lang: string;
    href: string;
    storeId: number;
}

export type TLoadParams = {
    safe?: boolean;
    reload?: boolean;
    dependencyLevel: EdependencyLevel;
    loader?: DependencyLoader;
    noCache?: boolean;
};

export enum EdependencyLevel {
    none = 10,
    listing = 20,
    details = 30
}

export type TLoadDependeciesParams = TLoadParams & { loader: DependencyLoader };

export type IOnLoadCallback = (model) => Promise<any> | undefined | void;

export abstract class Model<D extends IModelDataV1 = IModelData, I extends string | number = string | number> implements IModel {
    /**
     * Model type name (product, category, ...)
     * @type {string}
     */
    static type: string;

    /**
     * Routable constant. Should be set to true for models that
     * may have own pages.
     * Routable model data must contain path.
     * @type {Boolean}
     */
    static routable = false;

    /**
     * Post load processing extension tool
     * usege:
     *
     * Model.onLoad(model => {})
     * @type {Map<string, IOnLoadCallback[]>}
     */

    private static onLoadActions = new Map<string, IOnLoadCallback[]>();

    /**
     * Max verbosity level
     * @type {Number}
     */
    maxVerbosity = 3;

    /**
     * Model verbosity (may not be loaded!)
     * @type {number}
     */
    verbosity = 0;

    protected _data: D | undefined;

    /**
     * Model data from BE
     * @type {D}
     */

    set data(data: D | undefined) {
        this.dataSetter(data);
    }

    /**
     * Data getter
     */
    get data(): D | undefined {
        return this.dataGetter();
    }

    // internal model cache
    protected cache: any = {};

    /**
     * Change subject
     */
    readonly change = new Subject<void>();

    @NgInject(App)
    readonly app!: App;

    /**
     * Model's store id
     */
    get storeId(): number {
        return this.data?.store || this.multisiteService.storeId;
    }

    /**
     * Workaround to access static property
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @return {string}
     */
    public get type(): string {
        return (<any>this.constructor).type;
    }

    /**
     * Workaround to access static property
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @return {string}
     */
    public get routable(): string {
        return (<any>this.constructor).routable;
    }
    /**
     * Request service
     */
    @NgInject(RequestService)
    protected requestService!: RequestService;

    @NgInject(MultisiteService)
    protected multisiteService!: MultisiteService;

    /**
     * Resources
     */
    get resources() {
        return this.app.resources;
    }

    get resource() {
        return this.resources[this.type];
    }

    /**
     * Adds onLoad callbacks
     * @param {IOnLoadCallback} cb
     */
    static onLoad(cb: IOnLoadCallback) {
        let list: IOnLoadCallback[];

        if (this.onLoadActions.has(this.type)) {
            list = this.onLoadActions.get(this.type)!;
        } else {
            list = [];
            this.onLoadActions.set(this.type, list);
        }

        list.push(cb);
    }

    /**
     * Class contructor
     * @param {number | string}  model id (not needed if model is singleton, see resource)
     */
    constructor(public id: I) {
        if (typeof id !== 'number' && typeof id !== 'string') {
            throw new Error('Invalid model id:' + id + ' (' + typeof id + ') for type: ' + this.type);
        }
    }

    /**
     * Model has v2 getter
     * @returns {boolean}
     */
    get hasV2(): boolean {
        return this.verbosity ? this.verbosity >= 2 : false;
    }

    /**
     * Model has v3 getter
     * @returns {boolean}
     */
    get hasV3(): boolean {
        return this.verbosity ? this.verbosity >= 3 : false;
    }

    /**
     * Load model data
     *
     * ### Simple usage example
     *
     * model.load(2, (data) => {
     *      ...
     * });
     *
     * IMPORTANT: Load request always returns success! If lead is with error then error flag will be set.
     *
     */

    private loadModelStatus?: {
        verbosity: number;
        params: TLoadParams;
        promise: Promise<void>;
        busy: boolean;
        error?: any;
        dependencyLoader?: DependencyLoader;
    };

    private getDependencyLevel(params: Partial<TLoadParams> | undefined, verbosity): EdependencyLevel {
        return (
            params?.dependencyLevel ||
            (verbosity == 2 ? EdependencyLevel.listing : verbosity > 2 ? EdependencyLevel.details : EdependencyLevel.none)
        );
    }

    async load(verbosity: number, params: Partial<TLoadParams> = {}): Promise<void> {
        try {
            const loadParams: TLoadParams = defaults(params, {
                safe: false,
                dependencyLevel: this.getDependencyLevel(params, verbosity)
            });
            let loader = params.loader || this.loadModelStatus?.dependencyLoader;

            await this.loadModel(verbosity, loadParams);
            if (loadParams.dependencyLevel > EdependencyLevel.none) {
                if (!params.loader || !params.loader.hasDependencies(this, loadParams)) {
                    if (!loader) {
                        loader = new DependencyLoader(this.type, this.id);
                        if (this.loadModelStatus) {
                            this.loadModelStatus.dependencyLoader = loader;
                            loader.promise.finally(() => {
                                if (this.loadModelStatus?.dependencyLoader === loader) {
                                    this.loadModelStatus!.dependencyLoader = undefined;
                                }
                            });
                        }
                    }
                    this.addDependenciesToLoader({ ...loadParams, loader });
                }
            }
            // do not block if loader is provided, it means we load it as dependncy and there is a parent promise wrapping it
            if (params.loader) {
                return;
            }
            if (loader?.hasWaitingPromises) {
                return loader.promise;
            }
        } catch (error) {
            if (!params.safe) {
                throw error;
            }
        }
    }

    /**
     * Load model data
     *
     * Loads model only, without dependencies
     * If model is being loaded on sufficient level it will return promise of that load
     * If model has already been loaded with higher verbosity it will not reload it unless requested
     */
    private async loadModel(verbosity: number = 1, params: TLoadParams): Promise<void> {
        if (this.isLoadNeeded(verbosity, params)) {
            const load = async () => {
                try {
                    await this.loadModelFromApi(verbosity);
                } catch (error) {
                    if (this.loadModelStatus?.verbosity === verbosity && !this.data) {
                        this.loadModelStatus.error = error || 'Unexpected error';
                    }
                }

                this.loadModelStatus!.busy = false;
            };

            this.loadModelStatus = {
                verbosity,
                params,
                promise: load(),
                busy: true
            };

            return this.loadModelStatus.promise;
        }

        if (this.loadModelStatus?.busy) {
            return this.loadModelStatus.promise;
        }
    }

    /**
     * Sends load request to API which resolves with model data
     */
    protected async loadModelFromApi(verbosity: number): Promise<void> {
        try {
            this.setData(await this.requestService.loadModel<D>({ id: this.id, type: this.type, verbosity }));
        } catch (err) {
            throw err;
        }
    }

    /**
     * Checks if load is needed
     * It is not needed if we already loaded model with higher verbosity or we are during the process
     *
     * Keep in mind that it does not check if model is loaded at all, only if it is needed to be loaded again
     */
    isLoadNeeded(verbosity: number, params?: Partial<TLoadParams>): boolean {
        return !this.loadModelStatus || this.loadModelStatus.busy || params?.reload || this.loadModelStatus.verbosity < verbosity;
    }

    protected addDependenciesToLoader(params: TLoadDependeciesParams): void {
        params.loader.registerDependencies(this, params);

        const onLoadActions = Model.onLoadActions.get(this.type);

        if (onLoadActions) {
            onLoadActions.forEach(cb => {
                const promise = cb(this);

                if (promise instanceof Promise) {
                    params.loader.add(promise);
                }
            });
        }
    }

    /**
     * Unloads model
     */
    unload(): void {
        this.data = undefined;
        this.verbosity = 0;
    }

    protected dataSetter(data: D | undefined) {
        this._data = data;
    }

    protected dataGetter() {
        return this._data;
    }

    setData(resultData: D) {
        this.set(resultData, resultData.verbosity);
    }

    /**
     * Data getter
     */
    get rawData(): D | undefined {
        return this.data;
    }

    protected next() {
        this.change.next();
    }

    /**
     * Set this model data.
     * If current model verbosity is higher data will not be changed.
     * If model is not loaded yet, it will be marked as loaded.
     *
     * @param {any}       model data to set
     * @param {number=3}  model verbosity
     */
    protected set(data: D, verbosity: number): void {
        if (verbosity && (!this.verbosity || this.verbosity <= verbosity)) {
            const verbosityChange = (this.verbosity || 0) < verbosity;
            this.verbosity = verbosity;
            const dataChange =
                verbosityChange ||
                !this._data ||
                (data && '_updated_at' in data
                    ? (this._data as any)._updated_at !== (data as any)._updated_at
                    : !isEqual(this._data, data));

            if (dataChange) {
                this.data = data;
            }

            this.registerRoute();

            // index model

            // Resource<Model<IModelData, string | number>>
            this.resources[this.type]?.index(this);

            if (dataChange) {
                this.next();
            }
        }
    }

    /**
     * If model is routable then it creates `route` model
     * with reference to this.
     *
     * This we will not have to search for this `route`
     * next time it is needed.
     *
     * Important: routable model data must contain path
     */
    registerRoute() {
        const data = this.data;
        if (this.routable && data && 'url' in data && typeof data.url === 'string' && this.storeId === this.multisiteService.storeId) {
            const url = data.url;
            this.resources.route?.getCreate(url).setData({
                id: url,
                verbosity: 1,
                type: 'route',
                target: this.id,
                targetType: this.type
            });
        }
    }

    /**
     * Readable name getter
     */
    get name() {
        return this.getName();
    }

    protected getName() {
        return this.data && this.data['name'];
    }
}
